import axios from "axios";
import { API_BASE_URL } from "../config";

const isDevelopment = import.meta.env.DEV || false;

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        "Content-Type": "application/json",
    },
    timeout: 30000, // 30 seconds
});

// Add auth token to requests
api.interceptors.request.use((config) => {
    const token = localStorage.getItem("auth_token");
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Retry logic helper
const retryRequest = async (
    error: any,
    retryCount: number = 0,
    maxRetries: number = 3,
): Promise<any> => {
    const config = error.config;

    // Don't retry if it's not a server error or if we've exceeded max retries
    if (
        !error.response ||
        error.response.status < 500 ||
        retryCount >= maxRetries
    ) {
        return Promise.reject(error);
    }

    // Don't retry on timeout
    if (error.code === "ECONNABORTED") {
        return Promise.reject(error);
    }

    // Wait before retrying (exponential backoff)
    const delay = Math.min(1000 * Math.pow(2, retryCount), 10000);
    await new Promise((resolve) => setTimeout(resolve, delay));

    // Retry the request
    config.__retryCount = retryCount + 1;
    return api(config);
};

// Handle auth errors and retry logic
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem("auth_token");
            localStorage.removeItem("user_data");
            // Don't redirect automatically - let components handle it
            return Promise.reject(error);
        }

        // Retry on server errors (5xx)
        if (error.response?.status >= 500) {
            const retryCount = error.config?.__retryCount || 0;
            return retryRequest(error, retryCount);
        }

        return Promise.reject(error);
    },
);

// Auth API
export const authAPI = {
    sendOtp: async (phone: string) => {
        const response = await api.post("/api/v1/auth/send-otp", {phone});
        return response.data;
    },

    verifyOtp: async (phone: string, otp: string) => {
        const response = await api.post("/api/v1/auth/verify-otp", {phone, otp});
        return response.data;
    },

    login: async (phone: string, password?: string, otp?: string) => {
        const response = await api.post("/api/v1/auth/login", {
            phone,
            password,
            otp,
        });
        return response.data;
    },

    register: async (
        phone: string,
        password: string,
        full_name: string,
        email?: string,
    ) => {
        const response = await api.post("/api/v1/auth/register", {
            phone,
            password,
            full_name,
            email: email || null,
        });
        return response.data;
    },

    /** Get SSO redirect URL with short-lived code (no main token in URL). */
    getSsoLink: async (next?: string): Promise<{ redirect_url: string }> => {
        const response = await api.post("/api/v1/auth/sso/link", next ? { next } : {});
        const data = response.data as { redirect_url: string };
        return {redirect_url: data.redirect_url};
    },
};

// Profile & subscription API (uses same backend as auth)
export const profileAPI = {
    getProfile: async () => {
        const response = await api.get("/api/v1/auth/profile");
        return response.data;
    },

    updateProfile: async (data: { full_name?: string; password?: string }) => {
        const response = await api.put("/api/v1/auth/profile", data);
        return response.data;
    },

    getCurrentPlan: async () => {
        const response = await api.get("/api/v1/plans/current");
        return response.data;
    },

    listPlans: async () => {
        const response = await api.get("/api/v1/plans/list");
        return response.data;
    },
    getTransactions: async (params?: { limit?: number; offset?: number }) => {
        const response = await api.get("/api/v1/payments/transactions", {
            params: params || {limit: 10, offset: 0},
        });
        return response.data;
    },
};

export type ConnectorCredentialRecord = {
    id: string;
    user_id: number;
    service_name: string;
    provider_id: string;
    credential_name?: string | null;
    is_active: boolean;
    metadata?: Record<string, unknown> | null;
    created_at: string;
    updated_at: string;
};

export const connectorsAPI = {
    listProviders: async () => {
        const response = await api.get("/api/v1/connectors/providers");
        return response.data;
    },
    getProvider: async (providerId: string) => {
        const response = await api.get(`/api/v1/connectors/providers/${encodeURIComponent(providerId)}`);
        return response.data;
    },
    testProvider: async (providerId: string) => {
        const response = await api.post(`/api/v1/connectors/providers/${encodeURIComponent(providerId)}/test`);
        return response.data as { ok?: boolean; status_code?: number; message?: string; details?: Record<string, unknown> };
    },
    listCredentials: async (params?: { service_name?: string; provider_id?: string }) => {
        const response = await api.get("/api/v1/connectors/credentials", { params });
        return response.data as ConnectorCredentialRecord[];
    },
    saveCredential: async (payload: {
        service_name: string;
        provider_id: string;
        token: string;
        credential_name?: string | null;
        overwrite?: boolean;
        metadata?: Record<string, unknown> | null;
    }) => {
        const response = await api.post("/api/v1/connectors/credentials", payload);
        return response.data as ConnectorCredentialRecord;
    },
    deleteCredential: async (credentialId: string) => {
        const response = await api.delete(`/api/v1/connectors/credentials/${encodeURIComponent(credentialId)}`);
        return response.data as { deleted: boolean; credential_id: string };
    },
};

// Chat API
export const chatAPI = {
    getChats: async (params?: { limit?: number; offset?: number }) => {
        const response = await api.get("/api/v1/chats", {params});
        return response.data;
    },

    createChat: async (title: string) => {
        const response = await api.post("/api/v1/chats", {title});
        return response.data;
    },

    updateChat: async (chatId: string, title: string) => {
        const response = await api.put(`/api/v1/chats/${chatId}`, {title});
        return response.data;
    },

    deleteChat: async (chatId: string) => {
        const response = await api.delete(`/api/v1/chats/${chatId}`);
        return response.data;
    },

    saveMessage: async (
        chatId: string,
        userMessage: string | Array<Record<string, unknown>>,
        assistantMessage: string,
    ) => {
        const response = await api.post(`/api/v1/chats/${chatId}/messages`, {
            user_message: userMessage,
            assistant_message: assistantMessage,
        });
        return response.data;
    },
    getChatMessages: async (chatId: string, page = 1, limit = 20) => {
        const response = await api.get(`/api/v1/chats/${chatId}/messages`, {
            params: {page, limit},
        });
        return response.data;
    },
    generateImage: async (
        messages: Array<{ role: string; content: any }>,
        model: string,
        abortSignal?: AbortSignal,
    ) => {
        const token = localStorage.getItem("auth_token");
        const response = await fetch(
            `${API_BASE_URL}/api/v1/chat/completions`,
            {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                signal: abortSignal,
                body: JSON.stringify({
                    model,
                    messages,
                    stream: false,
                }),
            },
        );

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(
                `HTTP error! status: ${response.status}, body: ${errorText}`,
            );
        }

        return await response.json();
    },

    sendMessage: async (
        messages: Array<{ role: string; content: any }>,
        model: string = "qwen3-32b",
        endpoint: string = "/api/v1/chat/completions",
        allowedProviderIds: string[] = [],
    ) => {
        const token = localStorage.getItem("auth_token");
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                model,
                messages,
                stream: false,
                temperature: 0.3,
                max_tokens: 2048,
                allowed_provider_ids: allowedProviderIds,
            }),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    },

    // Streaming message function with manual parsing (no external lib dependency)
    sendMessageStream: async function* (
        messages: Array<{ role: string; content: any }>,
        abortSignal?: AbortSignal,
        model: string = "qwen3-32b",
        endpoint: string = "/api/v1/chat/completions",
        allowedProviderIds: string[] = [],
        interactionResponse?: Record<string, unknown>,
        sessionId?: string,
    ) {
        const token = localStorage.getItem("auth_token");
        const headers = {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
        };

        // console.log('[SSE] Starting stream request to:', `${API_BASE_URL}${endpoint}`);

        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
            method: "POST",
            headers,
            signal: abortSignal,
                body: JSON.stringify({
                    model,
                    messages,
                    stream: true,
                    temperature: 0.3,
                    max_tokens: 2048,
                    repetition_penalty: 1.1,
                    frequency_penalty: 0.5,
                    allowed_provider_ids: allowedProviderIds,
                    interaction_response: interactionResponse,
                    session_id: sessionId,
                }),
            });

        // console.log('[SSE] Response status:', response.status, response.statusText);

        if (!response.ok) {
            const errorText = await response.text();
            console.error("[SSE] Error response:", errorText);
            throw new Error(
                `HTTP error! status: ${response.status}, body: ${errorText}`,
            );
        }

        if (!response.body) {
            throw new Error("No response body reader available");
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        try {
            while (true) {
                if (abortSignal?.aborted) {
                    reader.cancel();
                    throw new DOMException("Aborted", "AbortError");
                }

                const {done, value} = await reader.read();

                if (done) {
                    // console.log('[SSE] Stream ended.');
                    break;
                }

                const chunk = decoder.decode(value, {stream: true});
                buffer += chunk;

                const lines = buffer.split("\n");
                // Keep the last line in buffer as it might be incomplete
                buffer = lines.pop() || "";

                for (const line of lines) {
                    const trimmedLine = line.trim();
                    if (!trimmedLine) continue;

                    if (trimmedLine.startsWith("data: ")) {
                        const data = trimmedLine.slice(6);
                        if (data === "[DONE]") continue;

                        try {
                            const parsed = JSON.parse(data);
                            yield parsed;
                        } catch (e) {
                            console.warn("[SSE] Failed to parse JSON:", data, e);
                        }
                    }
                }
            }
        } catch (error) {
            console.error("[SSE] Stream error:", error);
            throw error;
        } finally {
            reader.releaseLock();
        }
    },
};

// Text-to-Speech API using Google Translate TTS (free, no API key needed)
export const ttsAPI = {
    // Convert text to speech using Google Translate TTS
    // This is a free service that doesn't require API key
    textToSpeech: async (
        text: string,
        language: string = "fa",
    ): Promise<Blob> => {
        try {
            // Google Translate TTS endpoint (free, no API key needed)
            // Limit text to 200 characters per request (Google Translate TTS limit)
            const maxLength = 200;
            const textChunks: string[] = [];

            // Split text into chunks if it's too long
            if (text.length > maxLength) {
                let currentChunk = "";
                const words = text.split(" ");

                for (const word of words) {
                    if ((currentChunk + " " + word).length <= maxLength) {
                        currentChunk += (currentChunk ? " " : "") + word;
                    } else {
                        if (currentChunk) {
                            textChunks.push(currentChunk);
                        }
                        currentChunk = word;
                    }
                }
                if (currentChunk) {
                    textChunks.push(currentChunk);
                }
            } else {
                textChunks.push(text);
            }

            // Map language codes
            const langMap: Record<string, string> = {
                "fa-IR": "fa",
                fa: "fa",
                en: "en",
                "en-US": "en",
            };
            const ttsLang = langMap[language] || "fa";

            // Fetch audio for each chunk and combine
            const audioBlobs: Blob[] = [];

            for (const chunk of textChunks) {
                const encodedText = encodeURIComponent(chunk);
                // Use Google Translate TTS endpoint (updated endpoint)
                const ttsUrl = `https://translate.google.com/translate_tts?ie=UTF-8&q=${encodedText}&tl=${ttsLang}&client=gtx`;

                try {
                    const response = await fetch(ttsUrl, {
                        method: "GET",
                        headers: {
                            Referer: "https://translate.google.com/",
                            "User-Agent":
                                "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36",
                        },
                    });

                    if (!response.ok) {
                        // If Google TTS fails, throw error to fallback to browser TTS
                        throw new Error(
                            `TTS request failed: ${response.status} ${response.statusText}`,
                        );
                    }

                    const audioBlob = await response.blob();

                    // Check if response is actually audio (not HTML error page)
                    if (audioBlob.type.startsWith("audio/") || audioBlob.size > 100) {
                        audioBlobs.push(audioBlob);
                    } else {
                        throw new Error("Invalid audio response from TTS service");
                    }

                    // Small delay between requests to avoid rate limiting
                    if (textChunks.length > 1) {
                        await new Promise((resolve) => setTimeout(resolve, 200));
                    }
                } catch (chunkError) {
                    // If any chunk fails, throw error to use fallback
                    throw new Error(`Failed to fetch TTS for chunk: ${chunkError}`);
                }
            }

            // Combine all audio blobs into one
            if (audioBlobs.length === 1) {
                return audioBlobs[0];
            } else {
                // Combine multiple audio blobs (they're all MP3 format)
                return new Blob(audioBlobs, {type: "audio/mpeg"});
            }
        } catch (error) {
            console.error("Error in Google Translate TTS:", error);
            throw error;
        }
    },
};

export default api;
